import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import TimeZoneKeyboardDateTimePicker from '../shared/DrawerInputs/TimeZoneDateTimePicker';
import moment from 'moment-timezone';
import { validateAuth } from '../../util/util';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { maxCharacterLength, fieldRequired } from '../../util/constants';
import { notificationSuccess, notificationError } from '../../redux/actions/app.js';

const MaintenanceBanner = ({ open, onClose }) => {
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [error, setError] = useState(null);
  const timezone = 'Pacific/Auckland';
  const dispatch = useDispatch();

  useEffect(() => {
    fetchMaintenanceBanner();
  }, []);

  const handleSave = () => {
    setError(null);
    if (message.length > 130) {
      setError(maxCharacterLength(130));
      return;
    }
    const payload = {
      maintenance_banner: {
        message,
        start_date: startDate ? startDate.toISOString() : null,
        end_date: endDate ? endDate.toISOString() : null,
        enabled,
        timezone,
      },
    };
    saveMaintenanceBanner(payload);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const saveMaintenanceBanner = (body) => {
    validateAuth((session) => {
      axios
        .post(`${process.env.REACT_APP_AWS_API_URL}/admin_settings/maintenance_banner`, body, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15 * 1000,
        })
        .then((response) => {
          dispatch(notificationSuccess('Maintenance banner saved successfully'));
        })
        .catch((error) => {
          dispatch(notificationError('Error saving maintenance banner'));
        });
    });
  };

  const fetchMaintenanceBanner = () => {
    validateAuth((session) => {
      axios
        .get(`${process.env.REACT_APP_AWS_API_URL}/admin_settings/maintenance_banner`, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15 * 1000,
        })
        .then((response) => {
          const banner = response.data.banner;
          if (!banner) {
            setMessage('');
            setStartDate(null);
            setEndDate(null);
            setEnabled(false);
            return;
          }
          setMessage(banner.message || '');
          setStartDate(moment(banner.start_date).isValid() ? moment(banner.start_date) : null);
          setEndDate(moment(banner.end_date).isValid() ? moment(banner.end_date) : null);
          setEnabled(banner.enabled);
        })
        .catch((error) => {
          dispatch(notificationError('Error fetching maintenance banner'));
        });
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Banner Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>Enable Banner</Typography>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
              label=""
            />
          </Box>
          <TextField
            error={!!error}
            label={`Banner message * (max 130 characters) ${message.length}/130`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={!enabled}
            sx={{ mb: 3 }}
            helperText={error}
          />
          <TimeZoneKeyboardDateTimePicker
            label="Start Date & Time"
            value={startDate}
            onChange={setStartDate}
            timezone={timezone}
            disabled={!enabled}
            minDate={moment()}
            type="maintenance_banner"
            maxDate={endDate || undefined}
          />

          <TimeZoneKeyboardDateTimePicker
            label="End Date & Time"
            value={endDate}
            onChange={setEndDate}
            timezone={timezone}
            disabled={!enabled}
            minDate={startDate || moment()}
            type="maintenance_banner"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <div className="row gap-lg">
          <Button
            variant="outlined"
            disableElevation
            color="tertiary"
            onClick={handleCancel}
            startIcon={<KeyboardBackspaceRoundedIcon />}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={enabled && !message.trim()}
            endIcon={<SaveIcon />}
          >
            Save
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceBanner;
